<template>
  <div class="card">
    <div class="card-header ps-7 pe-5">
      <h3 class="card-title align-items-start flex-column">
        {{ translate("audit_planning_list_identification_") }}
        <span class="text-muted mt-1 fw-bold fs-7"
          >{{ records }} {{ translate("Records_Found") }}</span
        >
      </h3>
    </div>
    <add-audit-planning
      :auditId="auditId"
      @cancelForm="cancelForm"
    />

    <div class="p-5">
      <div>
        <div class="card-toolbar mb-5">
          <!--begin::Toolbar-->

          <div class="d-flex justify-content-end align-items-center">
            <div>
              <button
                type="button"
                class="btn btn-primary btn-sm"
                data-bs-toggle="modal"
                data-bs-target="#add-audit-planning"
              >
                {{ translate("audit_planning_list_new") }}
              </button>
              <button
                v-show="selectedPlanning.length > 0"
                @click="deletePlanning($event)"
                type="button"
                class="btn bg-light bg-hover-secondary btn-sm border position-relative ms-2"
              >
                {{ translate("Delete_Select")
                }}<span
                  class="position-absolute top-0 start-100 translate-middle badge badge-circle badge-danger"
                  >{{ selectedPlanning.length }}</span
                >
              </button>
            </div>
          </div>
        </div>
      </div>
      <DataTable
        :value="auditList"
        dataKey="Id"
        @select-all-change="onSelectAllChange"
        @sort="onSort($event)"
        @filter="onFilter($event)"
        @row-select="onRowSelect"
        @row-unselect="onRowUnselect"
        v-model:selection="selectedPlanning"
        v-model:filters="filters"
        filterDisplay="row"
        :globalFilterFields="[
          'customerName',
          'auditorName',
          'projectName',
          'dearchTerm',
          'auditTypeName',
          'status',
          'approval',
        ]"
        :lazy="true"
        :paginator="false"
        :loading="loading"
        :totalRecords="totalRecords"
        :selectAll="selectAll"
        removableSort
        class="rounded"
        responsiveLayout="scroll"
      >
        <Column
          selectionMode="multiple"
          headerStyle="width: 3em"
        ></Column>
        <Column
          field="CustomerName"
          :header="translate('CustomerAdd_UCCompanyName')"
          filterField="customerName"
          filterMatchMode="contains"
          ref="customerName"
          :sortable="true"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              name="customerName"
              type="text"
              @keydown.enter="filterCallback()"
              class="p-column-filter"
              :placeholder="translate('Search')"
              @keyup="inputNull($event)"
            />
          </template>
        </Column>
        <Column
          field="AuditorName"
          :header="translate('audit_planning_list_audit_name')"
          filterField="auditorName"
          filterMatchMode="contains"
          ref="auditorName"
          :sortable="true"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              name="auditorName"
              type="text"
              @keydown.enter="filterCallback()"
              class="p-column-filter"
              :placeholder="translate('Search')"
              @keyup="inputNull($event)"
            />
          </template>
        </Column>

        <Column
          field="ProjectName"
          :header="translate('audit_planning_list_project')"
          filterField="projectName"
          filterMatchMode="contains"
          ref="projectName"
          :sortable="true"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              name="projectName"
              type="text"
              @keydown.enter="filterCallback()"
              class="p-column-filter"
              :placeholder="translate('Search')"
              @keyup="inputNull($event)"
            />
          </template>
        </Column>

        <Column
          field="LocationName"
          :header="translate('audit_planning_list_location')"
          filterField="searchTerm"
          filterMatchMode="contains"
          ref="searchTerm"
          :sortable="true"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              name="searchTerm"
              type="text"
              @keydown.enter="filterCallback()"
              class="p-column-filter"
              :placeholder="translate('Search')"
              @keyup="inputNull($event)"
            />
          </template>
        </Column>

        <Column
          field="AuditTypeName"
          :header="translate('audit_planning_list_audit_type')"
          filterField="auditTypeName"
          filterMatchMode="contains"
          ref="auditTypeName"
          :sortable="true"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              name="auditTypeName"
              type="text"
              @keydown.enter="filterCallback()"
              class="p-column-filter"
              :placeholder="translate('Search')"
              @keyup="inputNull($event)"
            />
          </template>
        </Column>
        <Column
          field="status"
          :header="translate('userGroup_list_column_status')"
          :showFilterMenu="false"
        >
          <template #body="{ data }">
            <span :class="'customer-badge status-' + data.Status">{{
              data.Status
            }}</span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <Dropdown
              v-model="filterModel.value"
              @change="filterCallback()"
              :options="statusList"
              :placeholder="translate('Search')"
              class="p-column-filter"
              :showClear="true"
            >
              <template #value="slotProps">
                <span
                  :class="'customer-badge status-' + slotProps.value"
                  v-if="slotProps.value"
                  >{{ slotProps.value.Value }}</span
                >

                <span v-else>{{ slotProps.placeholder }}</span>
              </template>
              <template #option="slotProps">
                <span
                  :class="'customer-badge status-' + slotProps.option.Value"
                  >{{ slotProps.option.Value }}</span
                >
              </template>
            </Dropdown>
          </template>
        </Column>

        <Column
          field="approval"
          dataType="boolean"
          filterField="status"
          filterMatchMode="contains"
          style="width: 4rem; text-align: center"
          class="p-title-status"
          filterMenuStyle="text-align:center"
          :showClearButton="false"
        >
          <template #body="{ data }">
            <a :title="translate('audit_pending_approval')">
              <i
                class="bi"
                :class="{
                  'bi-x-circle text-solid fs-1': data.StatusValue == 30,
                }"
              ></i
            ></a>
          </template>
        </Column>

        <Column
          field="Id"
          :rowEditor="true"
          bodyStyle="text-align:center"
        >
          <template #body="{ data }">
            <div class="d-flex justify-content-end">
              <router-link
                :to="{
                  path: `/audit/audit-details/${data.Id}`,
                }"
              >
                <a
                  v-if="data.StatusValue == 30 || data.StatusValue == 100"
                  :title="translate('Detail')"
                  class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                  style="pointer-event: none"
                >
                  <span class="svg-icon svg-icon-3">
                    <inline-svg src="media/icons/duotune/abstract/abs015.svg" />
                  </span>
                </a>
              </router-link>
              <a
                v-if="data.StatusValue == 10"
                class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                :title="translate('Update')"
                @click="getUpDatePlanning(data.Id)"
                data-bs-toggle="modal"
                data-bs-target="#add-audit-planning"
              >
                <span class="svg-icon svg-icon-3">
                  <inline-svg src="media/icons/duotune/art/art005.svg" />
                </span>
              </a>

              <a
                class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm"
                :title="translate('Delete')"
                @click="deletePlanning($event, data)"
              >
                <span class="svg-icon svg-icon-3">
                  <inline-svg src="media/icons/duotune/general/gen027.svg" />
                </span>
              </a>
            </div>
            <ConfirmPopup></ConfirmPopup>
          </template>
        </Column>
      </DataTable>

      <div class="mt-5">
        <Pagination
          @onPage="onPage"
          :TotalData="records"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import addAuditPlanning from "@/components/Audit/Modals/AddAuditPlaning.vue";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import * as Yup from "yup";
import { useConfirm } from "primevue/useconfirm";

//Store Tanımlaması
const store = useStore();
const platform = store.state.platform;
const confirm = useConfirm();
//Router Tanımlaması
const router = useRouter();

// Multi Language
const { t, te, locale } = useI18n();
const translate = (text) => {
  if (te(text)) {
    return t(text);
  } else {
    return text;
  }
};

//Add planning Modaldan Gelen Emit
const cancelForm = () => {
  auditId.value = 0;
};

//Değişkenler

let selectedPlanning = ref([]);
const selectAll = ref(false);
const totalRecords = ref(0);
const lazyParams = ref({});
const loading = computed(
  () => store.getters["AuditPlanningModule/_getLoading"]
);
const records = computed(
  () => store.getters["AuditPlanningModule/_getTotalRecords"]
);

// Update için Seçilen Müşterinin Tanımlanması
const auditId = ref(0);
const getUpDatePlanning = (Id) => {
  auditId.value = Id;
};

//Data Table Filter Tanımlaması
const filters = ref({
  auditorName: { value: null, matchMode: "contains" },
  customerName: { value: null, matchMode: "contains" },
  projectName: { value: null, matchMode: "contains" },
  searchTerm: { value: null, matchMode: "contains" },
  auditTypeName: { value: null, matchMode: "contains" },
  status: { value: null, matchMode: "contains" },
});

// Data Table Eventları
const onPage = (e) => {
  lazyParams.value.page = e.step.value;
  lazyParams.value.pageSize = e.pageSize.value;
  getData(lazyParams.value);
};

const onSort = (event) => {
  lazyParams.value.sortColumn = event.sortField;
  lazyParams.value.sortDirection = event.sortOrder === 1 ? "asc" : "desc";
  getData(lazyParams.value);
};

const onFilter = () => {
  lazyParams.value.customerName = filters.value.customerName.value;
  lazyParams.value.auditorName = filters.value.auditorName.value;
  lazyParams.value.projectName = filters.value.projectName.value;
  lazyParams.value.searchTerm = filters.value.searchTerm.value;
  lazyParams.value.auditTypeName = filters.value.auditTypeName.value;
  if (filters.value.status.value === null) {
    lazyParams.value.status = 0;
  } else {
    lazyParams.value.status = filters.value.status.value.Id;
  }

  getData(lazyParams.value);
};

const onSelectAllChange = (event) => {
  if (event.checked) {
    selectAll.value = true;
    selectedPlanning.value = auditList.value;
  } else {
    selectAll.value = false;
    selectedPlanning.value = [];
  }
};

const onRowSelect = () => {
  selectAll.value = selectedPlanning.value.length === totalRecords.value;
};

const onRowUnselect = () => {
  selectAll.value = false;
};

const inputNull = (event) => {
  if (event.target.value === "") {
    lazyParams.value[event.target.name] = "";
    getData(lazyParams.value);
  }
};

// Customer Delete İşlemi
const deletePlanning = (event, Item) => {
  let obj = [];
  let list = [];
  if (Item !== undefined) {
    obj.push(Item.Id);
    list.push(obj);
    list.push(lazyParams.value);
  } else {
    selectedPlanning.value.forEach((item) => {
      obj.push(item.Id);
    });
    list.push(obj);
    list.push(lazyParams.value);
  }
  let message = ref("");
  if (obj.length > 1) {
    message.value = translate("confirm_message_plans");
  } else {
    message.value = translate("confirm_message_plan");
  }

  confirm.require({
    target: event.currentTarget,
    message: message.value,
    icon: "pi pi-exclamation-triangle",
    acceptLabel: translate("customer_location_confirm_acceptLabel"),
    rejectLabel: translate("customer_location_confirm_rejectLabel"),
    acceptClass: "btn btn-sm btn-primary",
    rejectClass: "btn btn-sm btn-light",
    accept: () => {
      store
        .dispatch("AuditPlanningModule/DELETE_AUDIT_PLANNING", list)
        .finally((selectedPlanning.value = []));
    },
    reject: () => {
      return;
    },
  });
};

//Data Çekme Metodo
const getData = (payLoad) => {
  store.dispatch("AuditPlanningModule/GET_ALL_AUDIT_PLANNING", payLoad);
};

const auditList = computed(
  () => store.getters["AuditPlanningModule/_getAuditList"]
);

const statusList = ref([]);
const getStatus = (payLoad) => {
  store
    .dispatch("AuditPlanningModule/GET_COMMON_AUDIT_STATUES", payLoad)
    .then((response) => {
      statusList.value = response;
    });
};

watch(locale, () => {
  setCurrentPageBreadcrumbs(translate("audit_planning_list_identification_"), [
    translate("audit_planning_list_identification"),
  ]);
});

onMounted(() => {
  lazyParams.value = {
    page: 1,
    pageSize: 10,
    sortColumn: null,
    sortDirection: null,
    customerName: null,
    auditorName: null,
    projectName: null,
    auditTypeName: null,
    searchTerm: null,
    status: 0,
  };
  setCurrentPageBreadcrumbs(translate("audit_planning_list_identification_"), [
    translate("audit_planning_list_identification"),
  ]);
  getStatus();
});

const statuses = ref(["Planlandı", "Tamamlandı", "new", "negotiation"]);
getData({ page: 1, pageSize: 10 });
</script>

<style></style>
